import { isEmptyValue } from './bookingGate';
import type { BookEventBranch, BookEventPayload } from './bookEvent.types';
import { EVENT_TYPES } from './bookEventServiceRules';

function canBookClassGuest(branch: BookEventBranch | null): boolean {
  const booking = branch?.features?.booking;
  return [booking?.enabled === true, (booking?.guest_bookings ?? 0) > 0].every(Boolean);
}

export function canBookGuest(
  event: BookEventPayload,
  branch: BookEventBranch | null
): boolean {
  if (isEmptyValue(branch)) {
    return false;
  }
  const guestRules: Partial<Record<NonNullable<BookEventPayload['type']>, () => boolean>> = {
    [EVENT_TYPES.CLASS]: () => canBookClassGuest(branch),
    [EVENT_TYPES.COURSE]: () => (branch?.features?.courses?.guest_bookings ?? 0) > 0,
  };
  const guestRule = guestRules[event.type ?? EVENT_TYPES.EVENT] ?? (() => false);
  return guestRule();
}

function toCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * `null` means the event does not tell us how many places are left, so guest
 * capacity must not be inferred from it. Returning 0 there would pin the guest
 * counter to zero even when the location allows guests.
 */
function seatsLeftForGuests(size: unknown, booked: unknown): number | null {
  const total = toCount(size);
  if (total === null) {
    return null;
  }
  return Math.max(0, total - (toCount(booked) ?? 0) - 1);
}

function remainingGuestCapacity(event: BookEventPayload): number | null {
  const capacityRules: Partial<
    Record<NonNullable<BookEventPayload['type']>, () => number | null>
  > = {
    [EVENT_TYPES.CLASS]: () => seatsLeftForGuests(event.size, event.booked),
    [EVENT_TYPES.COURSE]: () =>
      seatsLeftForGuests(event.scheduleToBook?.size, event.scheduleToBook?.booked),
  };
  const capacityRule = capacityRules[event.type ?? EVENT_TYPES.EVENT] ?? (() => 0);
  return capacityRule();
}

/**
 * Locations can send `guest_bookings` as an on/off flag instead of an
 * allowance. `null` means the location sets no allowance of its own, which is
 * different from an allowance of zero.
 */
function toGuestLimit(value: unknown): number | null {
  const limit = toCount(value);
  return limit !== null && limit > 0 ? limit : null;
}

function classGuestLimit(branch: BookEventBranch | null): number | null {
  return toGuestLimit(branch?.features?.booking?.guest_bookings);
}

function courseGuestLimit(branch: BookEventBranch | null): number | null {
  return toGuestLimit(branch?.features?.courses?.guest_bookings);
}

function configuredGuestLimit(
  event: BookEventPayload,
  branch: BookEventBranch | null
): number | null {
  const limits: Partial<Record<NonNullable<BookEventPayload['type']>, () => number | null>> = {
    [EVENT_TYPES.CLASS]: () => classGuestLimit(branch),
    [EVENT_TYPES.COURSE]: () => courseGuestLimit(branch),
  };
  const guestLimit = limits[event.type ?? EVENT_TYPES.EVENT] ?? (() => null);
  return guestLimit();
}

export function maxGuests(event: BookEventPayload, branch: BookEventBranch | null): number {
  const capacity = remainingGuestCapacity(event);
  const branchLimit = configuredGuestLimit(event, branch);
  if (capacity === null && branchLimit === null) {
    // Neither the event nor the location bounds the party, so there is no
    // number we could honour: offering guests here would only fail server-side.
    return 0;
  }
  if (capacity === null) {
    return branchLimit ?? 0;
  }
  if (branchLimit === null) {
    return capacity;
  }
  return Math.min(capacity, branchLimit);
}
